import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Tag, X } from 'lucide-react';
import { productColorOptions } from '../utils/colors';

const GroupManager = ({ groups, fetchGroups, API_URL, getAuthHeaders }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'success' });
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    color: 'blue'
  });
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);

  const saveGroup = async () => {
    if (!newGroup.name || !newGroup.name.trim()) {
      setAlertModal({ show: true, message: 'Group name is required', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/groups`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newGroup)
      });

      if (response.ok) {
        setNewGroup({ name: '', description: '', color: 'blue' });
        setShowAddModal(false);
        fetchGroups();
        setAlertModal({ show: true, message: 'Group added successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to add group', type: 'error' });
      }
    } catch (error) {
      console.error('Error saving group:', error);
      setAlertModal({ show: true, message: 'Failed to save group', type: 'error' });
    }
  };

  const updateGroup = async () => {
    if (!editingGroup || !editingGroup.name || !editingGroup.name.trim()) {
      setAlertModal({ show: true, message: 'Group name is required', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/groups/${editingGroup.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: editingGroup.name,
          description: editingGroup.description,
          color: editingGroup.color
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingGroup(null);
        fetchGroups();
        setAlertModal({ show: true, message: 'Group updated successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to update group', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating group:', error);
      setAlertModal({ show: true, message: 'Failed to update group', type: 'error' });
    }
  };

  const deleteGroup = async () => {
    if (!deletingGroup) return;

    try {
      const response = await fetch(`${API_URL}/groups/${deletingGroup.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setDeletingGroup(null);
        fetchGroups();
        setAlertModal({ show: true, message: 'Group deleted successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to delete group', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      setAlertModal({ show: true, message: 'Failed to delete group', type: 'error' });
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Tag className="w-8 h-8 text-purple-400" />
          <div>
            <h1 className="text-2xl font-bold">Group Management</h1>
            <p className="text-sm text-gray-400">Organize products into groups for easier filtering</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Group
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
          <Tag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-400 mb-1">No groups yet</h3>
          <p className="text-sm text-gray-500 mb-4">Create your first group to organize products</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
          >
            Create Group
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map((group) => (
            <div
              key={group.id}
              className="bg-gray-900 rounded-lg border border-gray-800 p-4 hover:border-gray-700 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1">{group.name}</h3>
                  {group.description && (
                    <p className="text-sm text-gray-400 mb-2">{group.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium bg-${group.color || 'blue'}-500/20 text-${group.color || 'blue'}-400 border border-${group.color || 'blue'}-500/30`}>
                      {group.color || 'blue'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingGroup(group);
                      setShowEditModal(true);
                    }}
                    className="p-2 hover:bg-gray-800 rounded transition-colors"
                    title="Edit Group"
                  >
                    <Edit2 className="w-4 h-4 text-gray-400 hover:text-white" />
                  </button>
                  <button
                    onClick={() => {
                      setDeletingGroup(group);
                      setShowDeleteModal(true);
                    }}
                    className="p-2 hover:bg-gray-800 rounded transition-colors"
                    title="Delete Group"
                  >
                    <Trash2 className="w-4 h-4 text-red-400 hover:text-red-300" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Group Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Add New Group</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Group Name</label>
                <input
                  type="text"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                  placeholder="Production, Staging, Testing..."
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                  placeholder="Group description"
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <select
                  value={newGroup.color}
                  onChange={(e) => setNewGroup({...newGroup, color: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                >
                  {productColorOptions.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveGroup}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Save Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditModal && editingGroup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Edit Group</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingGroup(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Group Name</label>
                <input
                  type="text"
                  value={editingGroup.name}
                  onChange={(e) => setEditingGroup({...editingGroup, name: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={editingGroup.description || ''}
                  onChange={(e) => setEditingGroup({...editingGroup, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <select
                  value={editingGroup.color}
                  onChange={(e) => setEditingGroup({...editingGroup, color: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                >
                  {productColorOptions.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingGroup(null);
                }}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={updateGroup}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingGroup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold mb-2">Delete Group</h2>
              <p className="text-gray-400">
                Are you sure you want to delete <span className="text-white font-semibold">"{deletingGroup.name}"</span>? 
                Products in this group will not be deleted, but their group assignment will be removed.
              </p>
            </div>

            <div className="p-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingGroup(null);
                }}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={deleteGroup}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">{alertModal.type === 'success' ? 'Success' : 'Error'}</h2>
              <p className="text-gray-400 mb-6">{alertModal.message}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => setAlertModal({ show: false, message: '', type: 'success' })}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManager;
